import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router';
import { useLoadFeatureFlags } from '~/singlePageApp/hooks/useLoadFeatureFlags';
import { getUserDataForEvent } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/utils/eventHelpers';
import { isNil, isNonEmptyString } from '~/utilities/type-guards';
import { Broadcast } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast';
import { liveStreamEventRoutes as LiveRoutes } from '~/singlePageApp/screens/LiveStreamScreen/liveStreamEventRoutes';
import { LoadingSpinner } from '~/singlePageApp/components/Studio/UI/LoadingSpinner';
import { match } from 'ts-pattern';
import { UnsupportedBrowser } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/components/EventEntry/UnsupportedBrowser';
import { getPanelistLink, isMobile } from '~/singlePageApp/screens/LiveStreamScreen/shared/utils';
import { cachedDetect } from '~/utilities/detect';
import { useLiveStreamPanelistRedirectLazyQuery } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/gql/__gql/LiveStreamPanelistRedirect.gql.gen';
import { useGetLiveJoinDataQuery } from '~/singlePageApp/screens/LiveStreamScreen/Join/__gql/LiveJoinData.gql.gen';
import type { ParticipantType } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/types/ParticipantType';
import { dangerous_markComponentAsRoutable } from '~/singlePageApp/App/routingHelpers';
import { isJoinRequired } from '~/singlePageApp/screens/LiveStreamScreen/Join/joinHelpers';

const detect = (cachedDetect as () => { safari: boolean; firefox: boolean })();
const isMobileDevice = isMobile();
const unsupported = detect.safari || detect.firefox || isMobileDevice;

type AccessState = 'denied' | 'granted' | 'pending' | 'unsupported';

/**
 * Route-level component that decides between redirecting to JoinPage or rendering Broadcast
 * based on feature flag and stored user data.
 */
export const ParticipantRouter = dangerous_markComponentAsRoutable(
  ({ participantType }: { participantType: ParticipantType }): JSX.Element => {
    const [access, setAccess] = useState<AccessState>(unsupported ? 'unsupported' : 'pending');

    const { hashedId = '', joinKey = '' } = useParams<{ hashedId: string; joinKey: string }>();

    const { flags, loading: loadingFlags } = useLoadFeatureFlags(['flag_live_studio_joinkit']);
    const isJoinKitEnabled = flags.flag_live_studio_joinkit.enabled;

    const { data: eventData, loading: loadingData } = useGetLiveJoinDataQuery({
      variables: { hashedId },
      skip: !hashedId,
    });

    const isLoading = loadingFlags || loadingData;

    const [checkHostPermissions] = useLiveStreamPanelistRedirectLazyQuery({
      variables: { hashedId },
    });

    useEffect(() => {
      if (participantType === 'host') {
        void checkHostPermissions({
          onCompleted: (permissionData) => {
            if (isNonEmptyString(permissionData.liveEventJoinKeyForPanelistRedirect)) {
              const url = getPanelistLink(
                hashedId,
                permissionData.liveEventJoinKeyForPanelistRedirect,
                isJoinKitEnabled,
              );
              window.location.href = `${url}?redirect=panelist`;
            }
          },
        });
      }
    }, [checkHostPermissions, hashedId, participantType, isJoinKitEnabled]);

    /**
     * Determine whether to redirect to join page or to render the broadcast
     * based on event status, feature flags, and local storage user data.
     */
    useEffect(() => {
      if (isLoading || unsupported || isNil(eventData?.liveStreamEvent)) {
        return;
      }

      if (!isJoinRequired(eventData.liveStreamEvent) || !isJoinKitEnabled) {
        setAccess('granted');
        return;
      }

      // For active/upcoming events, check if user has stored data from previous JoinKit flow
      const userData = getUserDataForEvent(hashedId, participantType);
      const hasStoredName = isNonEmptyString(userData?.username);

      setAccess(hasStoredName ? 'granted' : 'denied');
    }, [isLoading, hashedId, participantType, eventData?.liveStreamEvent, isJoinKitEnabled]);

    return match(access)
      .with('unsupported', () => <UnsupportedBrowser isMobile={isMobileDevice} />)
      .with('pending', () => <LoadingSpinner />)
      .with('granted', () => <Broadcast userType={participantType} />)
      .with('denied', () => (
        <Navigate
          to={
            participantType === 'host'
              ? LiveRoutes.hostJoinPage(hashedId)
              : LiveRoutes.joinPage(hashedId, joinKey)
          }
          replace={true}
        />
      ))
      .exhaustive();
  },
);
